import React, { useState, useEffect } from 'react';
import { X, Package } from 'lucide-react';
import { ResponsiveImage } from './ResponsiveImage';

/**
 * PaidOptionsModal component
 * Modal for selecting paid options when adding a product to cart
 * Props:
 *  - product: product object with paid_options array
 *  - onConfirm(productWithOptions): called when user confirms selection
 *  - onClose(): called when user cancels
 */
export const PaidOptionsModal = ({ product, onConfirm, onClose }) => {
    const [selectedPaidOptions, setSelectedPaidOptions] = useState([]);

    // Reset selected options when product changes
    useEffect(() => {
        setSelectedPaidOptions([]);
    }, [product?.product_id]);

    if (!product) return null;

    // Toggle a paid option
    const togglePaidOption = (option) => {
        setSelectedPaidOptions(prev => {
            const exists = prev.some(opt => opt.name === option.name);
            if (exists) {
                return prev.filter(opt => opt.name !== option.name);
            } else {
                return [...prev, option];
            }
        });
    };

    // Calculate total price including selected options
    const calculateTotalPrice = () => {
        const basePrice = parseFloat(product.price) || 0;
        const optionsPrice = selectedPaidOptions.reduce((sum, opt) => sum + (parseFloat(opt.price) || 0), 0);
        return basePrice + optionsPrice;
    };

    const totalPrice = calculateTotalPrice();

    const handleConfirm = () => {
        const productWithOptions = {
            ...product,
            selectedPaidOptions: selectedPaidOptions
            // Don't modify price - it should remain as base product price
            // CartModal will calculate the total with options
        };
        onConfirm(productWithOptions);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadein"
            onClick={onClose}
        >
            <div
                className="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-mekomit-100 bg-gradient-to-r from-mekomit-500 to-mekomit-400 shrink-0 rounded-t-3xl">
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                        בחר תוספות
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                        aria-label="סגור"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {/* Product info */}
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-mekomit-100">
                        {product.images && product.images.length > 0 ? (
                            <div className="w-16 h-16 rounded-lg overflow-hidden border border-mekomit-100 shrink-0">
                                <ResponsiveImage
                                    src={product.images[0]}
                                    alt={product.name}
                                    aspectRatio="aspect-square"
                                    placeholderClassName="bg-gradient-to-br from-mekomit-50 to-mekomit-100"
                                />
                            </div>
                        ) : (
                            <div className="w-16 h-16 rounded-lg bg-mekomit-100 flex items-center justify-center border border-mekomit-200 shrink-0">
                                <Package className="w-8 h-8 text-mekomit-300" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-mekomit-900 truncate">{product.name}</h3>
                            <p className="text-sm text-mekomit-600">מחיר בסיס: ₪{product.price}</p>
                        </div>
                    </div>

                    {/* Paid Options Section */}
                    {product.paid_options && product.paid_options.length > 0 && (
                        <div>
                            <h4 className="text-sm font-bold text-mekomit-700 mb-3">תוספות בתשלום</h4>
                            <div className="space-y-2">
                                {product.paid_options.map((option, idx) => {
                                    const isSelected = selectedPaidOptions.some(opt => opt.name === option.name);
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => togglePaidOption(option)}
                                            className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                                                isSelected
                                                    ? 'border-mekomit-500 bg-mekomit-50'
                                                    : 'border-mekomit-200 bg-white hover:border-mekomit-300 hover:bg-mekomit-25'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                                    isSelected
                                                        ? 'border-mekomit-500 bg-mekomit-500'
                                                        : 'border-mekomit-300'
                                                }`}>
                                                    {isSelected && (
                                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium text-mekomit-900">{option.name}</span>
                                            </div>
                                            <span className="text-sm font-bold text-mekomit-700">+₪{parseFloat(option.price).toFixed(2)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedPaidOptions.length > 0 && (
                                <div className="mt-3 p-3 bg-mekomit-50 border border-mekomit-200 rounded-lg">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="font-medium text-mekomit-700">סה"כ תוספות:</span>
                                        <span className="font-bold text-mekomit-900">
                                            +₪{selectedPaidOptions.reduce((sum, opt) => sum + parseFloat(opt.price), 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with action buttons */}
                <div className="px-4 sm:px-6 py-4 border-t border-mekomit-100 bg-white shadow-inner sticky bottom-0 rounded-b-3xl">
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleConfirm}
                            className="bg-gradient-to-r from-mekomit-500 to-mekomit-400 hover:from-mekomit-600 hover:to-mekomit-500 text-white font-bold py-3 px-6 rounded-xl transition-all shadow w-full"
                        >
                            הוסף לסל - ₪{totalPrice.toFixed(2)}
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-white hover:bg-mekomit-50 text-mekomit-700 font-medium py-2 px-6 rounded-xl transition-all border border-mekomit-200 w-full"
                        >
                            ביטול
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaidOptionsModal;
